import usePureState from '@/hooks/usePureState';
import { useGetRootPageQuery, useUpdateOrCreateRootPageMutation } from '@/queries/rootPages.queries';
import IRootPage from '@/types/IRootPage';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

export function useRootPageDetails() {
	const toast = useToast();
	const { state, dispatch } = usePureState({ errors: {} });
	const { id } = useParams<{ id: string }>();
	const { t } = useTranslation();
	const query = useGetRootPageQuery({ id });
	const updateOrCreateMutation = useUpdateOrCreateRootPageMutation();

	function handleChange(value: Partial<IRootPage>) {
		return updateOrCreateMutation
			.mutateAsync({ ...query.data!, ...value })
			.then(() => {
				dispatch({ errors: {} });
			})
			.catch((e) => {
				if (e.response?.data?.invalidParams) {
					e.response.data.invalidParams.forEach((invalidParam: { name: string; reason: string }) => {
						dispatch({ errors: { [invalidParam.name]: invalidParam.reason } });
					});

					toast({
						isClosable: true,
						title: t('Error'),
						status: 'error',
						description: t(_invalidParamsToString(e.response.data.invalidParams)),
					});
				}
			});
	}

	return {
		state,
		isLoading: updateOrCreateMutation.isPending || query.isPending,
		query,
		handleChange,
	};
}

function _invalidParamsToString(invalidParams: Array<{ name: string; reason: string }>): string {
	return invalidParams[0]?.reason || '';
}
